import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Button,
  DatePicker,
  Select,
  TimePicker,
  Input,
  message,
  Card,
  Spin,
  Tag,
  Space,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  SearchOutlined,
  ScanOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  useAttendanceQuery,
  useSaveAttendanceMutation,
  useQuickClockMutation,
  useSectionAccess,
} from "../../Services";
import { PageHeader } from "../../components";
import { HRAction } from "../../Types";

const { Option } = Select;

export const AttendanceRegisterPage = () => {
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(dayjs());
  const [searchText, setSearchText] = useState("");
  const [attendanceData, setAttendanceData] = useState<any[]>([]);
  const [quickIdInput, setQuickIdInput] = useState("");
  const quickInputRef = useRef<any>(null);

  const dateStr = selectedDate.format("YYYY-MM-DD");

  const {
    data: response,
    isLoading,
    refetch,
  } = useAttendanceQuery({
    date: dateStr,
    search: searchText,
  });

  const { isAllowed } = useSectionAccess("hr");

  const saveMutation = useSaveAttendanceMutation();
  const quickClockMutation = useQuickClockMutation();

  // Populate daily attendance table data
  useEffect(() => {
    if (response?.success && response.data) {
      setAttendanceData(
        response.data.map((item: any) => ({
          employee_id: item.employee_id,
          first_name: item.first_name,
          last_name: item.last_name,
          designation: item.designation || "Staff",
          nic: item.nic || "",
          status: item.status || "Present",
          check_in: item.check_in ? dayjs(item.check_in, "HH:mm:ss") : null,
          check_out: item.check_out ? dayjs(item.check_out, "HH:mm:ss") : null,
          worked_hours: item.worked_hours || 0,
          ot_hours: item.ot_hours || 0,
          notes: item.notes || "",
        })),
      );
    }
  }, [response]);

  const handleStatusChange = (employeeId: string, status: string) => {
    setAttendanceData((prev) =>
      prev.map((row) => {
        if (row.employee_id === employeeId) {
          const updatedRow = { ...row, status };
          if (status === "Absent") {
            updatedRow.check_in = null;
            updatedRow.check_out = null;
            updatedRow.worked_hours = 0;
            updatedRow.ot_hours = 0;
          } else if (!row.check_in) {
            updatedRow.check_in = dayjs("08:30", "HH:mm");
          }
          return updatedRow;
        }
        return row;
      }),
    );
  };

  const handleTimeChange = (
    employeeId: string,
    field: "check_in" | "check_out",
    time: dayjs.Dayjs | null,
  ) => {
    setAttendanceData((prev) =>
      prev.map((row) => {
        if (row.employee_id === employeeId) {
          return { ...row, [field]: time };
        }
        return row;
      }),
    );
  };

  const handleNotesChange = (employeeId: string, notes: string) => {
    setAttendanceData((prev) =>
      prev.map((row) => {
        if (row.employee_id === employeeId) {
          return { ...row, notes };
        }
        return row;
      }),
    );
  };

  const handleSaveAll = async () => {
    try {
      const records = attendanceData.map((row) => ({
        employee_id: row.employee_id,
        status: row.status,
        check_in: row.check_in ? row.check_in.format("HH:mm:ss") : null,
        check_out: row.check_out ? row.check_out.format("HH:mm:ss") : null,
        notes: row.notes || null,
      }));

      const payload = {
        date: dateStr,
        records,
      };

      const res = await saveMutation.mutateAsync(payload);
      if (res?.success) {
        message.success(
          `Attendance register for ${dateStr} saved successfully!`,
        );
        refetch();
      } else {
        message.error(res?.message || "Failed to save attendance records.");
      }
    } catch (err: any) {
      message.error(err?.message || "An error occurred while saving.");
    }
  };

  const handleQuickClockSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!quickIdInput.trim()) return;

    try {
      const res = await quickClockMutation.mutateAsync({
        identifier: quickIdInput.trim(),
        date: dateStr,
      });

      if (res?.success) {
        message.success(res?.message || "Clocking recorded!");
        refetch();
      } else {
        message.error(res?.message || "Failed to process clocking.");
      }
    } catch (err: any) {
      message.error(err?.message || "Network error occurred.");
    } finally {
      setQuickIdInput("");
      setTimeout(() => {
        if (quickInputRef.current) {
          quickInputRef.current.focus();
        }
      }, 100);
    }
  };

  // Daily Table Columns
  const columns = [
    {
      title: "Employee",
      key: "name",
      width: 230,
      render: (_: any, rec: any) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-xs flex-shrink-0">
            {rec.first_name[0]?.toUpperCase()}
            {rec.last_name[0]?.toUpperCase()}
          </div>
          <div>
            <p className="font-semibold text-gray-800 text-sm m-0 leading-tight">
              {rec.first_name} {rec.last_name}
            </p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-xs text-gray-400 font-medium">
                {rec.designation}
              </span>
              {rec.nic && (
                <>
                  <span className="text-gray-300 text-xs">•</span>
                  <span className="text-xs text-gray-400 font-mono">
                    {rec.nic}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 130,
      render: (_: any, rec: any) => (
        <Select
          value={rec.status}
          onChange={(val) => handleStatusChange(rec.employee_id, val)}
          className="w-full"
        >
          <Option value="Present">
            <Tag color="success">Present</Tag>
          </Option>
          <Option value="Absent">
            <Tag color="error">Absent</Tag>
          </Option>
          <Option value="Late">
            <Tag color="warning">Late</Tag>
          </Option>
          <Option value="Half Day">
            <Tag color="processing">Half Day</Tag>
          </Option>
        </Select>
      ),
    },
    {
      title: "First In",
      key: "check_in",
      width: 120,
      render: (_: any, rec: any) => (
        <TimePicker
          value={rec.check_in}
          onChange={(time) =>
            handleTimeChange(rec.employee_id, "check_in", time)
          }
          format="HH:mm"
          placeholder="08:30"
          className="w-full"
          disabled={rec.status === "Absent"}
        />
      ),
    },
    {
      title: "Last Out",
      key: "check_out",
      width: 120,
      render: (_: any, rec: any) => (
        <TimePicker
          value={rec.check_out}
          onChange={(time) =>
            handleTimeChange(rec.employee_id, "check_out", time)
          }
          format="HH:mm"
          placeholder="17:30"
          className="w-full"
          disabled={rec.status === "Absent"}
        />
      ),
    },
    {
      title: "Hours",
      key: "worked_hours",
      width: 100,
      render: (_: any, rec: any) => (
        <span className="font-medium text-gray-700">
          {rec.worked_hours > 0
            ? `${parseFloat(rec.worked_hours).toFixed(2)}`
            : "-"}
        </span>
      ),
    },
    {
      title: "OT (Hrs)",
      key: "ot_hours",
      width: 100,
      render: (_: any, rec: any) => (
        <span
          className={
            rec.ot_hours > 0 ? "font-bold text-green-600" : "text-gray-400"
          }
        >
          {rec.ot_hours > 0 ? `+${parseFloat(rec.ot_hours).toFixed(2)}` : "-"}
        </span>
      ),
    },
    {
      title: "Notes / Remarks",
      key: "notes",
      render: (_: any, rec: any) => (
        <Input
          value={rec.notes}
          onChange={(e) => handleNotesChange(rec.employee_id, e.target.value)}
          placeholder="Add comments..."
          allowClear
        />
      ),
    },
  ];



  return (
    <div className="p-6 min-h-screen bg-gray-50/50">
      <PageHeader
        title="Attendance & Roster Terminal"
        subText="Log daily clocking logs for all employees"
        handleButtons={[
          ...(isAllowed(HRAction.ManageAttendance)
            ? [
                {
                  icon: SaveOutlined,
                  title: "Save Register",
                  onClick: () => handleSaveAll(),
                  loading: saveMutation.isPending,
                },
              ]
            : []),
          {
            icon: ReloadOutlined,
            title: "Refresh",
            onClick: () => refetch(),
          },
        ]}
        searchBar={
          <div className="flex gap-3">
            <DatePicker
              value={selectedDate}
              onChange={(date) => date && setSelectedDate(date)}
              format="YYYY-MM-DD"
              allowClear={false}
              className="w-40 h-9"
            />
            <Input
              placeholder="Filter daily register..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              className="w-60 h-9"
            />
          </div>
        }
      />

      <Space direction="vertical" size={16} className="w-full mt-2">
        <Card
          className="border-gray-200 shadow-sm rounded-lg"
          title={
            <div className="flex items-center gap-2 text-blue-600 font-semibold">
              <ScanOutlined />
              <span>Quick Clock-In / Clock-Out Terminal</span>
            </div>
          }
        >
          <form
            onSubmit={handleQuickClockSubmit}
            className="flex gap-x-3 max-w-xl"
          >
            <Input
              ref={quickInputRef}
              placeholder="Scan Barcode or enter NIC / Phone Number and press Enter..."
              value={quickIdInput}
              onChange={(e) => setQuickIdInput(e.target.value)}
              disabled={quickClockMutation.isPending}
              prefix={<ScanOutlined style={{ color: "#bfbfbf" }} />}
              size="middle"
              className="flex-1"
            />
            {isAllowed(HRAction.ManageAttendance) && (
              <Button
                type="primary"
                htmlType="submit"
                icon={<ArrowRightOutlined />}
                loading={quickClockMutation.isPending}
                size="middle"
              >
                Clocking
              </Button>
            )}
          </form>
        </Card>

        <Card
          className="border-gray-200 shadow-sm rounded-lg overflow-hidden"
          bodyStyle={{ padding: 0 }}
        >
          <Spin spinning={isLoading || saveMutation.isPending}>
            <Table
              dataSource={attendanceData}
              columns={columns}
              rowKey="employee_id"
              pagination={false}
              scroll={{ x: 900 }}
              locale={{
                emptyText: "No active employees found to record attendance.",
              }}
            />
          </Spin>
        </Card>
      </Space>
    </div>
  );
};
